import { createContext, PropsWithChildren, useMemo, useState } from 'react'
import { Company, Deal } from '@/types'
import { Box, Typography } from '@mui/material'
import { Loading } from '@/components/loading'
import { ErrorOutline } from '@mui/icons-material'
import { useListDeals, useReadCompany } from '@/api/accounting-dealer'
import { compareDesc } from 'date-fns'

const STORAGE_COMPANY_KEY = 'selected_company'

type CompanyContextType = {
  company: Company
  deal: Deal
  setCompany: (companyKey: string) => void
}

export const CompanyContext = createContext({} as CompanyContextType)

export function CompanyContextProvider({ children }: PropsWithChildren) {
  const STORED_COMPANY_KEY = localStorage.getItem(STORAGE_COMPANY_KEY)

  const [selectedCompany, setSelectedCompany] = useState(STORED_COMPANY_KEY)

  const { data: deals, isLoading: readingDeals } = useListDeals()

  const deal = useMemo(() => {
    if (readingDeals || !deals?.length) return

    const stored = deals.find((deal) => deal.company.key === selectedCompany)

    if (stored) {
      return stored
    }

    localStorage.setItem(STORAGE_COMPANY_KEY, deals[0].company.key)

    return deals[0]
  }, [deals, selectedCompany, readingDeals])

  const handleSelectCompany = (companyKey: string) => {
    if (!deals) return

    const deal = deals.find((deal) => deal?.company.key === companyKey)
    setSelectedCompany(deal?.company.key || '')
    localStorage.setItem(STORAGE_COMPANY_KEY, deal?.company.key || '')
  }

  const { data: company, isLoading: readingCompany } = useReadCompany(
    { companyKey: deal?.company?.key || '' },
    { enabled: Boolean(deal?.company?.key) },
  )

  const mostRecentTaxRegime = useMemo(() => {
    if (!company?.company_tax_regimes?.length) return

    return [...company.company_tax_regimes].sort((a, b) => {
      if (!a.start_date && !b.start_date) return 0
      if (!a.start_date) return 1
      if (!b.start_date) return -1

      return compareDesc(new Date(a.start_date), new Date(b.start_date))
    })[0]
  }, [company?.company_tax_regimes])

  if (readingDeals || readingCompany) {
    return <Loading />
  }

  if (!deal || !company) {
    return (
      <Box textAlign="center">
        <ErrorOutline color="error" fontSize="large" />
        <Typography color="gray">
          Não foi possível carregar as empresas.
        </Typography>
      </Box>
    )
  }

  return (
    <CompanyContext.Provider
      value={{
        company: { ...company, tax_regime: mostRecentTaxRegime },
        deal,
        setCompany: handleSelectCompany,
      }}
    >
      {children}
    </CompanyContext.Provider>
  )
}
